import type { AccessorFn, Cell, ColumnMeta, Row, ColumnDef as TanstackColumnDef } from '@tanstack/react-table';
import { useReactTable, getCoreRowModel, createColumnHelper } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';

import { useDraggableColumns, useToggle } from 'af-utils/react.util';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import BaseCell from './BaseCell';
import HeaderGroups from './HeaderGroups';
import styles from './styles.module.scss';
import type { ColumnDef, Column as LeafColumn, ColumnGroup, FooterButton, SimpleTableProps, FooterCellPlacement, CalculatedHeaderData } from './types';

const CELL_MIN_WIDTH = 100;
const DISPLAY_CELL_WIDTH = 50;
const ACCESSOR_CELL_MIN_WIDTH = 100;

export type TableRef = {
	scrollToBottomRow: () => void;
};

type SimpleTableColumnMeta = {
	isFixed?: boolean;
};

function isColumnGroup<T>(col: ColumnDef<T>): col is ColumnGroup<T> {
	return 'columns' in col;
}

function isDisplayColumn<T>(col: LeafColumn<T>): col is LeafColumn<T> & { isDisplayColumn: true; } {
	return col.isDisplayColumn === true;
}

function SimpleTableInner<T>(props: SimpleTableProps<T>, ref: React.Ref<TableRef>) {
	const {
		rows,
		className,
		columns,
		label,
		labelTooltip,
		footerButtonsLeft = [],
		footerButtonsRight = [],
		headerButtons = [],
		rowClassName,
		footerComponent,
		emptyTableMessage,
		onCellClicked,
		arrayAccessorName,
		getRowId,
		getCellClassName,
		height,
		sideDrawer,
		drawerTitle,
		footerRow,
		readonly,
		onRowClicked,
		virtualizeRows = false,
		titleClassName,
	} = props;

	const tableBodyRef = React.useRef<Nullable<HTMLDivElement>>(null);
	const headerRef = React.useRef<Nullable<HTMLElement>>(null);

	const { value: isDrawerOpen, setToTrue: openDrawer, setToFalse: closeDrawer } = useToggle(false);

	const { cellWidthsMap, columnRefs, onColumnDrag } = useDraggableColumns({
		minColumnWidth: CELL_MIN_WIDTH,
	});

	const setHeaderReference = React.useCallback((headerId: string) => (_ref: HTMLDivElement) => { columnRefs.current[headerId] = _ref; }, [columnRefs]);

	const computeFooterCellRange = React.useCallback((placement: FooterCellPlacement, leafIds: string[]) => {
		const indexOf = (id: string) => leafIds.findIndex((leafId) => leafId === id);

		if (placement.kind === 'at') {
			const idx = indexOf(placement.leafId);
			if (idx < 0) {
				return null;
			}
			return [idx, idx + 1] as const;
		}

		if (placement.kind === 'span') {
			const start = indexOf(placement.fromLeafId);
			if (start < 0) {
				return null;
			}
			const end = Math.min(leafIds.length, start + Math.max(placement.span, 1));
			return [start, end] as const;
		}

		// between
		const fromIdx = placement.fromLeafId ? indexOf(placement.fromLeafId) : -1;
		const toIdx = placement.toLeafId ? indexOf(placement.toLeafId) : leafIds.length;

		const start = Math.max(fromIdx + 1, 0);
		const end = Math.max(Math.min(toIdx, leafIds.length), start);

		return [start, end] as const;
	}, []);

	const externallyControlledCells = React.useMemo(() => {
		return columns.reduce<Record<string, true>>((_agg, _column) => {
			if (!isColumnGroup(_column) && _column.externalClickControl) {
				_agg[_column.id] = true;
			} else if (isColumnGroup(_column)) {
				for (const childColumn of _column.columns) {
					_agg[childColumn.id] = true;
				}
			}
			return _agg;
		}, {});
	}, [columns]);

	const columnDefs = React.useMemo(() => {
		const columnHelper = createColumnHelper<T>();

		const isEmptyHeader = (header: ColumnDef<T>['header']) => {
			return header === '' || header === null || header === undefined;
		};

		const normalizeColumn = (col: ColumnDef<T>): ColumnDef<T> => {
			if (!isColumnGroup(col)) {
				return col;
			}

			const normalizedChildren = col.columns.map(normalizeColumn);

			if (isEmptyHeader(col.header) && normalizedChildren.length === 1) {
				return normalizedChildren[0];
			}

			return {
				...col,
				columns: normalizedChildren,
			};
		};

		const getColumnMeta = (col: LeafColumn<T>): ColumnMeta<T, unknown> => ({
			...(col.meta as ColumnMeta<T, unknown>),
			isFixed: col.isFixed,
		});

		const build = (col: ColumnDef<T>): TanstackColumnDef<T> => {
			if (isColumnGroup(col)) {
				return columnHelper.group({
					id: col.id,
					header: col.header ?? '',
					columns: col.columns.map(build),
					meta: col.meta as ColumnMeta<T, unknown>,
				});
			}

			if (isDisplayColumn(col)) {
				return columnHelper.display({
					id: col.id,
					cell: col.cell,
					header: col.header ?? '',
					size: col.size,
					meta: getColumnMeta(col),
				});
			}

			return columnHelper.accessor(col.accessor as AccessorFn<T, unknown>, {
				id: col.id,
				cell: col.cell,
				header: col.header ?? '',
				size: col.size,
				meta: getColumnMeta(col),
				enableSorting: col.enableSorting,
				sortingFn: col.sortingFn,
				sortDescFirst: col.sortDescFirst,
			});
		};

		return columns.map(normalizeColumn).map(build);
	}, [columns]);

	const table = useReactTable({
		columns: columnDefs,
		data: rows ?? [],
		getCoreRowModel: getCoreRowModel(),
		getRowId,
	});

	const calculatedHeaderData = React.useMemo<CalculatedHeaderData<T>>(() => {
		return {
			headerGroups: table.getHeaderGroups(),
			leafColumns: table.getVisibleLeafColumns(),
		};
		// we want this to recompute on column definition changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table, columnDefs]);

	// Use ResizeObserver so it updates when column structure changes
	const [headerOffset, setHeaderOffset] = React.useState(0);

	React.useLayoutEffect(() => {
		if (!virtualizeRows) return;
		const el = headerRef.current;
		if (!el) return;

		// Initial measurement
		setHeaderOffset(el.getBoundingClientRect().height);

		// ResizeObserver for re-measurement when header height changes
		const observer = new ResizeObserver(() => {
			setHeaderOffset(el.getBoundingClientRect().height);
		});
		observer.observe(el);

		return () => observer.disconnect();
	}, [columnDefs, virtualizeRows]);

	React.useImperativeHandle(ref, () => {
		return {
			scrollToBottomRow: () => {
				if (tableBodyRef.current) {
					tableBodyRef.current.scrollTo({
						top: tableBodyRef.current.scrollHeight,
						behavior: 'smooth',
					});
				}
			},
		};
	}, []);

	const gridTemplateColumns = React.useMemo(() => {
		const leaf = table.getVisibleLeafColumns();

		return leaf
			.map((col) => {
				const dragged = cellWidthsMap[col.id]?.width;
				if (dragged) {
					return `${dragged}px`;
				}
				const size = col.columnDef.size;
				if (size) {
					return typeof size === 'number' ? `${size}px` : size;
				}
				const isDisplay = !col.accessorFn;
				if (isDisplay) {
					return `${DISPLAY_CELL_WIDTH}px`;
				}
				return `minmax(${ACCESSOR_CELL_MIN_WIDTH}px, 1fr)`;
			})
			.join(' ');
		// recompute when the column structure changes — `table` is a stable ref
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cellWidthsMap, table, columnDefs]);

	const fixedLeafColumnStyles = React.useMemo(() => {
		const leaf = table.getVisibleLeafColumns();

		let left = 0;

		return leaf.reduce<Record<string, React.CSSProperties>>((acc, col) => {
			const meta = col.columnDef.meta as SimpleTableColumnMeta | undefined;

			const dragged = cellWidthsMap[col.id]?.width;
			const size = dragged ?? col.columnDef.size;
			const width = typeof size === 'number'
				? size
				: undefined;

			if (meta?.isFixed) {
				acc[col.id] = {
					position: 'sticky',
					left,
					zIndex: 3,
				};

				left += width ?? ACCESSOR_CELL_MIN_WIDTH;
			}

			return acc;
		}, {});
	}, [cellWidthsMap, table]);

	const renderFooterRow = React.useCallback(() => {
		if (!footerRow) {
			return null;
		}

		const leafCols = table.getVisibleLeafColumns();
		const leafIds = leafCols.map((c) => c.id);
		if (!leafIds.length) {
			return null;
		}

		const placed = footerRow.cells
			.map((cell) => {
				const range = computeFooterCellRange(cell.placement, leafIds);
				if (!range) {
					return null;
				}
				const [start, end] = range;
				if (end <= start) {
					return null;
				}
				return { cell, start, end };
			})
			.filter((x): x is NonNullable<typeof x> => !!x)
			.sort((a, b) => a.start - b.start);

		const nonOverlapping: typeof placed = [];
		let lastEnd = 0;
		for (const p of placed) {
			if (p.start < lastEnd) {
				continue;
			}
			nonOverlapping.push(p);
			lastEnd = p.end;
		}

		const nodes: React.ReactNode[] = [];
		let cursor = 0;

		for (const cellDef of nonOverlapping) {
			if (cellDef.start > cursor) {
				nodes.push(
					<div
						className={styles['simple-table__cell']}
						key={`gap:${cursor}-${cellDef.start}`}
						role="cell"
						style={{ gridColumn: `span ${cellDef.start - cursor}` }}
					/>
				);
			}

			const span = cellDef.end - cellDef.start;

			nodes.push(
				<div
					className={[
						styles['simple-table__cell'],
						cellDef.cell.className ?? '',
					].join(' ')}
					key={`cell:${cellDef.cell.id}`}
					role="cell"
					style={{ gridColumn: `span ${span}` }}
				>
					{cellDef.cell.cell}
				</div>
			);

			cursor = cellDef.end;
		}

		if (cursor < leafIds.length) {
			nodes.push(
				<div
					className={styles['simple-table__cell']}
					key={`gap:${cursor}-${leafIds.length}`}
					role="cell"
					style={{ gridColumn: `span ${leafIds.length - cursor}` }}
				/>
			);
		}

		return (
			<div
				className={[
					styles['simple-table__row'],
					footerRow.className ?? '',
				].join(' ')}
				role="row"
				style={{ gridTemplateColumns }}
			>
				{nodes}
			</div>
		);
	}, [computeFooterCellRange, footerRow, gridTemplateColumns, table]);

	const onCellClick = React.useCallback((cellName: string) => {
		if (!!onCellClicked) {
			onCellClicked(cellName);
		}
	}, [onCellClicked]);

	const cellMapper = React.useCallback((_cell: Cell<T, unknown>) => {
		let name = '';

		const fixedStyle = fixedLeafColumnStyles[_cell.column.id];

		if (arrayAccessorName) {
			const accessorKey = _cell.column.columnDef['accessorKey' as keyof typeof _cell.column.columnDef];
			const key = accessorKey ?? _cell.column.id;
			name = `${arrayAccessorName}.${_cell.row.index}.${key}`;
		}

		const resolvedClassName = [
			getCellClassName?.(_cell) ?? '',
			fixedStyle ? styles['simple-table__cell--fixed'] : '',
			fixedStyle ? styles['simple-table__cell--fixed-last'] : '',
			readonly ? styles.readonly : '',
		].filter(Boolean).join(' ');

		return (
			<BaseCell<T>
				cell={_cell}
				className={resolvedClassName}
				key={_cell.id}
				name={name}
				onCellClick={!externallyControlledCells[_cell.column.id] ? onCellClick : undefined}
				style={fixedStyle}
			/>
		);
	}, [arrayAccessorName, externallyControlledCells, fixedLeafColumnStyles, getCellClassName, onCellClick, readonly]);

	const onRowClickAction = React.useCallback((_row: Row<T>) => () => {
		if (onRowClicked) {
			onRowClicked(_row);
		}
	}, [onRowClicked]);

	const rowsMapper = React.useCallback((_row: Row<T>) => {
		const rowClassNameArray = [styles['simple-table__row']];

		if (rowClassName) {
			typeof rowClassName === 'string' ? rowClassNameArray.push(rowClassName) : rowClassNameArray.push(rowClassName(_row));
		}

		return (
			<div
				className={rowClassNameArray.join(' ')}
				key={_row.id}
				onClick={onRowClickAction(_row)}
				style={{ gridTemplateColumns }}
			>
				{_row.getVisibleCells().map(cellMapper)}
			</div>
		);
	}, [rowClassName, onRowClickAction, gridTemplateColumns, cellMapper]);

	const buttonsMapper = (_button: FooterButton) => {
		return (
			<Button
				icon={_button.iconName}
				key={_button.label}
				label={_button.label}
				onClick={_button.onClick}
				size="small"
				style="link"
			/>
		);
	};

	const showTitleRow = headerButtons.length || label;

	const resolvedClassName = React.useMemo(() => {
		const classNames = [styles['simple-table']];

		if (className) {
			classNames.push(className);
		}

		if (height) {
			classNames.push(styles['simple-table--with-height']);
		}

		return classNames.join(' ');
	}, [className, height]);

	const resolvedStyle = React.useMemo<React.CSSProperties>(() => {
		if (!height) {
			return {};
		}

		return {
			height,
		};
	}, [height]);

	const resolvedTitleRow = React.useMemo(() => {
		if (!titleClassName) {
			return styles['simple-table__title-row'];
		}
		return `${styles['simple-table__title-row']} ${titleClassName}`;
	}, [titleClassName]);

	const hasDrawer = React.useMemo(() => !!sideDrawer, [sideDrawer]);

	const virtualizer = useVirtualizer({
		count: (rows ?? []).length,
		getScrollElement: () => tableBodyRef.current,
		estimateSize: () => 26,
		overscan: 10,
		scrollMargin: headerOffset,
	});

	const renderVirtualRows = React.useCallback(() => {
		const virtualItems = virtualizer.getVirtualItems();
		const sortedRows = table.getSortedRowModel().rows;
		const virtualRowElements: React.ReactNode[] = [];

		for (const virtualItem of virtualItems) {
			const tanstackRow = sortedRows[virtualItem.index];
			if (!tanstackRow) {
				continue;
			}

			const rowElement = rowsMapper(tanstackRow);
			const clonedRow = React.cloneElement(rowElement, {
				'data-index': virtualItem.index,
				style: {
					...rowElement.props.style,
					position: 'absolute',
					top: 0,
					transform: `translateY(${virtualItem.start - virtualizer.options.scrollMargin}px)`,
					left: 0,
					right: 0,
					key: `${tanstackRow.id}:${gridTemplateColumns}`,
				} as React.CSSProperties,
				ref: virtualizer.measureElement,
			});
			virtualRowElements.push(clonedRow);
		}

		return virtualRowElements;
	}, [virtualizer, table, rowsMapper, gridTemplateColumns]);

	const hasGroupedHeaders = calculatedHeaderData.headerGroups.length > 1;

	return (
		<div className={styles['simple-table-shell']}>
			<div className={styles['simple-table-shell__table']}>
				<div className={resolvedClassName} style={resolvedStyle}>
					<div className={styles['simple-table__layout']}>
						<div className={styles['simple-table__main']}>
							{showTitleRow && (
								<div className={resolvedTitleRow}>
									{label && (
										<div className={styles['simple-table__title-row__label']}>
											<SegmentLabel
												label={label}
												tooltip={labelTooltip}
												tooltipPosition={!!labelTooltip ? 'right' : undefined}
											/>
										</div>
									)}
									{!!headerButtons.length && (
										<div className={styles['simple-table__title-row__buttons']}>
											{headerButtons.map(buttonsMapper)}
										</div>
									)}
								</div>
							)}

							<div
								className={[
									styles['simple-table__body'],
									hasGroupedHeaders ? styles['simple-table__body--grouped'] : '',
								].filter(Boolean).join(' ')}
								ref={tableBodyRef}
							>
								<HeaderGroups<T>
									fixedLeafColumnStyles={fixedLeafColumnStyles}
									gridTemplateColumns={gridTemplateColumns}
									headerData={calculatedHeaderData}
									headerRef={headerRef as unknown as React.Ref<Nullable<HTMLDivElement>>}
									onColumnDrag={onColumnDrag}
									setHeaderReference={setHeaderReference}
								/>

								{virtualizeRows && (rows ?? []).length > 0 ? (
									<div
										style={{ position: 'relative', height: virtualizer.getTotalSize(), width: 'max-content', minWidth: '100%' }}
									>
										{renderVirtualRows()}
									</div>
								) : (
									table.getSortedRowModel().rows.length
										? table.getSortedRowModel().rows.map(rowsMapper)
										: (
											<div className={styles['simple-table__empty-table-message']}>
												{emptyTableMessage ?? 'No Rows Found.'}
											</div>
										)
								)}

								{!!footerRow && renderFooterRow()}
							</div>

							{footerComponent && (
								<div className={styles['simple-table__footer-component']}>
									{footerComponent()}
								</div>
							)}

							{(!!footerButtonsLeft.length || !!footerButtonsRight.length) && (
								<div className={styles['simple-table__footer']}>
									<div className={styles['simple-table__footer-buttons']}>
										{footerButtonsLeft.map(buttonsMapper)}
									</div>
									<div className={styles['simple-table__footer-buttons']}>
										{footerButtonsRight.map(buttonsMapper)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{hasDrawer && (
				<div className={styles['simple-table-shell__rail']}>
					<div className={styles['simple-table-shell__rail-header']}>
						{!isDrawerOpen ? (
							<span className={`icon-fly-out-arrow ${styles['simple-table-shell__icon-button']}`}
								onClick={openDrawer}
							/>
						) : null}
					</div>
				</div>
			)}

			{hasDrawer && isDrawerOpen && (
				<div className={styles['simple-table-shell__drawer']}>
					<div className={styles['simple-table-shell__drawer-header']}>

						<div className={styles['simple-table-shell__drawer-title']}>
							{drawerTitle ?? ''}
						</div>
						<span
							className={`icon-fly-in-arrow ${styles['simple-table-shell__icon-button']}`}
							onClick={closeDrawer}
						/>
					</div>

					<div className={styles['simple-table-shell__drawer-content']}>
						{sideDrawer ? sideDrawer() : null}
					</div>
				</div>
			)}
		</div>
	);

}

export const SimpleTable = React.memo(React.forwardRef(SimpleTableInner)) as <T>(props: SimpleTableProps<T> & { ref?: React.Ref<TableRef>; })
	=> ReturnType<typeof SimpleTableInner>;
